import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../contexts/CartContext';
import { useSiteConfig } from '../contexts/SiteConfigContext';
import { Trash2, Plus, Minus, Tag, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function CartPage() {
  const { cart, removeFromCart, updateQuantity } = useCart();
  const { config } = useSiteConfig();
  const navigate = useNavigate();

  const [couponCode, setCouponCode] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(() => {
    try {
      const raw = localStorage.getItem('applied_coupon');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });

  const freeShippingThreshold = config?.free_shipping_threshold || 50;
  const shippingCost = config?.shipping_cost || 3.90;
  const baseShipping = cart.total >= freeShippingThreshold ? 0 : shippingCost;
  const shipping = appliedCoupon?.free_shipping ? 0 : baseShipping;
  const discount = appliedCoupon?.discount || 0;
  const totalWithShipping = Math.max(cart.total + shipping - discount, 0);
  const amountLeftForFreeShipping = freeShippingThreshold - cart.total;

  // Recalculate coupon discount when subtotal changes
  useEffect(() => {
    if (!appliedCoupon) return;
    if (cart.total < (appliedCoupon.min_order_amount || 0)) {
      localStorage.removeItem('applied_coupon');
      setAppliedCoupon(null);
      toast.info('Se ha retirado el cupón por no alcanzar el pedido mínimo');
      return;
    }
    if (appliedCoupon.discount_type === 'percentage') {
      const newDiscount = +(cart.total * (appliedCoupon.discount_value / 100)).toFixed(2);
      if (newDiscount !== appliedCoupon.discount) {
        const updated = { ...appliedCoupon, discount: newDiscount };
        setAppliedCoupon(updated);
        localStorage.setItem('applied_coupon', JSON.stringify(updated));
      }
    }
  }, [cart.total, appliedCoupon]);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setApplyingCoupon(true);
    try {
      const res = await axios.post(`${API}/coupons/validate`, {
        code: couponCode.trim(),
        subtotal: cart.total
      });
      setAppliedCoupon(res.data);
      localStorage.setItem('applied_coupon', JSON.stringify(res.data));
      setCouponCode('');
      toast.success(`Cupón "${res.data.code}" aplicado`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cupón no válido');
    }
    setApplyingCoupon(false);
  };

  const handleRemoveCoupon = () => {
    localStorage.removeItem('applied_coupon');
    setAppliedCoupon(null);
    toast.info('Cupón eliminado');
  };

  const handleRemoveItem = async (productId) => {
    await removeFromCart(productId);
    toast.success('Producto eliminado del carrito');
  };

  const handleCheckout = () => {
    if (cart.items && cart.items.length > 0) {
      navigate('/checkout');
    } else {
      toast.error('Tu carrito está vacío');
    }
  };

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-4xl sm:text-5xl font-light text-[#374963] mb-12" data-testid="cart-page-title">
          Carrito de Compras
        </h1>

        {!cart.items || cart.items.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.04)]" data-testid="empty-cart-message">
            <p className="text-[#73706e] text-lg mb-6">Tu carrito está vacío</p>
            <Link
              to="/catalogo"
              className="inline-block px-6 py-3 bg-[#374963] text-white rounded-lg hover:bg-[#4A5F7D] transition-colors"
              data-testid="go-to-catalog-button"
            >
              Ir al Catálogo
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Free shipping banner */}
            {amountLeftForFreeShipping > 0 ? (
              <div className="bg-[#785534] text-white p-4 rounded-lg text-center" data-testid="free-shipping-banner">
                Añade <strong>{amountLeftForFreeShipping.toFixed(2)}€</strong> más y consigue envío GRATIS
              </div>
            ) : (
              <div className="bg-green-600 text-white p-4 rounded-lg text-center" data-testid="free-shipping-achieved">
                ¡Tienes envío GRATIS! 🎉
              </div>
            )}

            {/* Cart Items */}
            <div className="bg-white rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.04)] p-6">
              {cart.items.map((item) => (
                <div
                  key={item.product_id}
                  className="flex flex-col sm:flex-row items-start sm:items-center gap-6 py-6 border-b border-[rgba(55,73,99,0.1)] last:border-0"
                  data-testid={`cart-item-${item.product_id}`}
                >
                  <img
                    src={item.image || 'https://via.placeholder.com/100'}
                    alt={item.name}
                    className="w-24 h-24 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-[#374963] mb-2">{item.name}</h3>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                        className="p-1 bg-[#374963] text-white rounded hover:bg-[#4A5F7D] transition-colors"
                        data-testid={`decrease-${item.product_id}`}
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="text-[#374963] font-semibold min-w-[30px] text-center" data-testid={`quantity-${item.product_id}`}>
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                        className="p-1 bg-[#374963] text-white rounded hover:bg-[#4A5F7D] transition-colors"
                        data-testid={`increase-${item.product_id}`}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-semibold text-[#374963] mb-2">
                      {(item.price * item.quantity).toFixed(2)}€
                    </p>
                    <button
                      onClick={() => handleRemoveItem(item.product_id)}
                      className="text-red-500 hover:text-red-700 transition-colors"
                      data-testid={`remove-item-${item.product_id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Cart Summary */}
            <div className="bg-white rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.04)] p-6">
              {/* Coupon Section */}
              <div className="mb-6 pb-6 border-b border-[rgba(55,73,99,0.1)]" data-testid="coupon-section">
                {appliedCoupon ? (
                  <div className="flex items-center justify-between bg-[#F5F1EC] p-3 rounded-lg" data-testid="applied-coupon">
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-[#785534]" />
                      <span className="font-semibold text-[#374963]">{appliedCoupon.code}</span>
                      <span className="text-sm text-[#73706e]">
                        {appliedCoupon.discount_type === 'percentage' && `-${appliedCoupon.discount_value}%`}
                        {appliedCoupon.discount_type === 'fixed' && `-${appliedCoupon.discount_value}€`}
                        {appliedCoupon.discount_type === 'free_shipping' && 'Envío gratis'}
                      </span>
                    </div>
                    <button
                      onClick={handleRemoveCoupon}
                      className="text-[#73706e] hover:text-red-500 transition-colors"
                      data-testid="remove-coupon-button"
                      aria-label="Quitar cupón"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Código de descuento"
                      className="flex-1 px-4 py-2 bg-[#374963] text-white placeholder-white/60 border border-[#374963] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#785534] uppercase"
                      data-testid="coupon-input"
                    />
                    <button
                      onClick={handleApplyCoupon}
                      disabled={applyingCoupon || !couponCode.trim()}
                      className="px-5 py-2 bg-[#374963] text-white rounded-lg hover:bg-[#4A5F7D] transition-colors disabled:opacity-50"
                      data-testid="apply-coupon-button"
                    >
                      {applyingCoupon ? 'Validando...' : 'Aplicar'}
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-[#73706e]">
                  <span>Subtotal:</span>
                  <span data-testid="cart-subtotal">{cart.total.toFixed(2)}€</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-green-600 font-medium" data-testid="cart-discount">
                    <span>Descuento ({appliedCoupon.code}):</span>
                    <span>-{discount.toFixed(2)}€</span>
                  </div>
                )}
                <div className="flex justify-between text-[#73706e]">
                  <span>Portes:</span>
                  <span data-testid="cart-shipping">
                    {shipping === 0 ? (
                      <span className="text-green-600 font-semibold">GRATIS</span>
                    ) : (
                      `${shipping.toFixed(2)}€`
                    )}
                  </span>
                </div>
                <div className="border-t border-[rgba(55,73,99,0.1)] pt-3 flex justify-between text-xl font-semibold text-[#374963]">
                  <span>Total:</span>
                  <span data-testid="cart-total">{totalWithShipping.toFixed(2)}€</span>
                </div>
              </div>

              <button
                onClick={handleCheckout}
                className="w-full py-4 bg-[#785534] text-white rounded-lg hover:bg-[#8f6640] transition-colors"
                data-testid="checkout-button"
              >
                Proceder al Pago
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CartPage;
